// frontend/src/GothamPostGameCount/GothamPostGameCount.jsx — /gotham-post-game-count: count the Gotham FC POS stock after
// a home game. Per size: Eatontown (Shopify now) − Sold at SI (the game's in-stadium sales) = Expected, against what was
// counted (typed in or uploaded from a CSV). Updating a product sets Eatontown to the count and SI Stadium to 0 for its
// counted sizes. Exports to Excel (with photos) and CSV.
// Mockup: https://claude.ai/artifact/V1ZGNDNNBfiVsgLUfEGKuJ
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gpgcApi } from './apiClient';
import { readCounts } from './csvCounts';
import { download } from '../ClubSalesHistory/apiClient';

const num = (n) => (Number(n) || 0).toLocaleString('en-US');
const plural = (n, w) => `${num(n)} ${w}${n === 1 ? '' : 's'}`;
const label = (p, v) => (v.title === 'One size' ? p.title : `${p.title}, ${v.title}`);
const timeNow = () => new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
const gameText = (g) => {
  if (!g) return '';
  const d = new Date(`${g.game_date}T12:00:00`);
  return `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} vs ${g.opponent}`;
};

// Counts and what was already sent to Shopify are kept in this browser, per game, so a reload mid-count loses nothing.
const store = {
  get(k, d) { try { return JSON.parse(localStorage.getItem(k)) || d; } catch { return d; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private window: counts live in memory only */ } },
};

function Dialog({ title, onClose, busy, children, footer }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [busy, onClose]);
  return (
    <div className="uh-pgc-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="uh-pgc-dialog" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
        <div className="hd"><h2>{title}</h2><button type="button" className="uh-btn slim" disabled={busy} onClick={onClose}>Close</button></div>
        <div className="bd">{children}</div>
        {footer && <div className="ft">{footer}</div>}
      </div>
    </div>
  );
}

function Photo({ src, alt, onZoom }) {
  if (!src) return <div className="uh-pgc-photo none"><span>No photo in Shopify</span></div>;
  return (
    <button type="button" className="uh-pgc-photo" onClick={onZoom} aria-label={`Enlarge photo of ${alt}`}>
      <img src={src} alt={alt} loading="lazy" /><span className="zoom">Tap to enlarge</span>
    </button>
  );
}

const fmtCount = (n) => (n == null ? <span className="uh-pgc-ns">Not stocked</span> : <span className={n < 0 ? 'uh-gpp-neg' : ''}>{num(n)}</span>);

export default function GothamPostGameCount() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({});        // variant id -> counted
  const [sent, setSent] = useState({});            // variant id -> { at, eatontown, stadium, counted } once set in Shopify
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [zoom, setZoom] = useState(null);
  const [review, setReview] = useState(null);      // product ids in the review dialog
  const [result, setResult] = useState(null);      // the update's stale / failed sizes
  const [upload, setUpload] = useState(null);      // the uploaded file's preview
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);
  const [photos, setPhotos] = useState(() => store.get('gpgc:paper-photos', true));   // the blank sheet: Excel with photos, or CSV
  const fileRef = useRef(null);
  useEffect(() => { store.set('gpgc:paper-photos', photos); }, [photos]);

  const gameDate = data && data.game ? data.game.game_date : null;
  const key = `gpgc:${gameDate || 'none'}`;

  const load = useCallback(async (date) => {
    setLoading(true);
    try { setData(await gpgcApi.sheet(date)); setError(''); } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(null); }, [load]);
  useEffect(() => { if (data) { setCounts(store.get(`${key}:counts`, {})); setSent(store.get(`${key}:sent`, {})); } }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data) store.set(`${key}:counts`, counts); }, [counts]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data) store.set(`${key}:sent`, sent); }, [sent]); // eslint-disable-line react-hooks/exhaustive-deps

  const products = useMemo(() => (data ? data.products : []), [data]);
  const byVariant = useMemo(() => Object.fromEntries(products.flatMap((p) => p.variants.map((v) => [v.id, { p, v }]))), [products]);

  // A size already sent shows the counts it was compared against, not Shopify's new ones.
  const eatShown = (v) => (sent[v.id] ? sent[v.id].eatontown : v.eatontown);
  const expected = (v) => (eatShown(v) || 0) - (v.sold || 0);
  const state = useCallback((p) => {
    let counted = 0; let upd = 0; let last = null; const pending = [];
    p.variants.forEach((v) => {
      if (sent[v.id]) { upd += 1; last = sent[v.id].at; counted += 1; } else if (counts[v.id] !== undefined) { counted += 1; if (v.tracked) pending.push(v); }
    });
    return { counted, upd, last, pending, total: p.variants.length, allUpd: upd === p.variants.length && upd > 0 };
  }, [counts, sent]);

  const shown = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return products.filter((p) => {
      const st = state(p);
      if (filter === 'todo' && st.counted === st.total) return false;
      if (filter === 'open' && st.allUpd) return false;
      return !qq || p.title.toLowerCase().includes(qq)
        || p.variants.some((v) => `${v.title} ${v.sku} ${v.barcode}`.toLowerCase().includes(qq));
    });
  }, [products, filter, q, state]);

  const totals = useMemo(() => {
    let sizes = 0; let upd = 0; let waiting = 0; const ready = [];
    products.forEach((p) => { const st = state(p); sizes += st.total; upd += st.upd; waiting += st.pending.length; if (st.pending.length) ready.push(p.id); });
    return { sizes, upd, waiting, ready };
  }, [products, state]);

  const setCount = (id, raw) => setCounts((c) => {
    const n = { ...c };
    if (raw === '') delete n[id]; else { const v = Math.floor(Number(raw)); if (Number.isFinite(v) && v >= 0) n[id] = v; }
    return n;
  });
  const fill = (p, fn) => setCounts((c) => {
    const n = { ...c };
    p.variants.forEach((v) => { if (sent[v.id]) return; const x = fn(v); if (x === null) delete n[v.id]; else n[v.id] = x; });
    return n;
  });
  const countAgain = (p) => setSent((s) => { const n = { ...s }; p.variants.forEach((v) => delete n[v.id]); return n; });

  // --- Update Shopify ---
  const reviewLines = (ids) => ids.flatMap((id) => { const p = products.find((x) => x.id === id); return state(p).pending.map((v) => ({ p, v })); });
  const applyNow = async () => {
    const rows = reviewLines(review);
    setBusy('apply');
    try {
      const r = await gpgcApi.apply(gameDate, rows.map(({ p, v }) => ({
        variant_id: v.id, inventory_item_id: v.inventory_item_id, label: label(p, v), tracked: v.tracked,
        eatontown: v.eatontown, stadium: v.stadium, counted: counts[v.id],
      })));
      const at = timeNow();
      const done = Object.fromEntries(r.set.map((ln) => [ln.variant_id, ln]));
      const fresh = Object.fromEntries(r.stale.filter((ln) => ln.now).map((ln) => [ln.variant_id, ln.now]));
      setSent((s) => {
        const n = { ...s };
        r.set.forEach((ln) => { n[ln.variant_id] = { at, eatontown: ln.eatontown, stadium: ln.stadium, counted: ln.counted }; });
        return n;
      });
      // Keep the page in step with Shopify: sent sizes now hold the count and 0; stale ones show what Shopify has now.
      setData((d) => ({ ...d, products: d.products.map((p) => ({ ...p, variants: p.variants.map((v) => {
        if (done[v.id]) return { ...v, eatontown: done[v.id].counted, stadium: v.stadium == null ? null : 0 };
        if (fresh[v.id]) return { ...v, ...fresh[v.id] };
        return v;
      }) })) }));
      setReview(null);
      if (r.stale.length || r.failed.length) setResult(r);
      else setNotice({ tone: 'ok', text: `Updated ${plural(r.set.length, 'size')} in Shopify: Eatontown set to your counts, SI Stadium to 0.` });
    } catch (e) {
      setNotice({ tone: 'bad', text: e.message }); setReview(null);
    }
    setBusy('');
  };

  // --- Files ---
  const exportFile = async (fmt, blank = false) => {
    setBusy(blank ? 'blank' : fmt);
    try {
      const all = { ...counts };
      Object.entries(sent).forEach(([id, s]) => { all[id] = s.counted; });
      download(await gpgcApi.exportFile(gameDate, fmt, blank ? {} : all, blank));
    } catch (e) { setNotice({ tone: 'bad', text: e.message }); }
    setBusy('');
  };
  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0]; e.target.value = '';
    if (!f) return;
    let text;
    try {
      text = /\.xlsx$/i.test(f.name) ? (await gpgcApi.readXlsx(f)).text : await f.text();
    } catch (err) {
      setNotice({ tone: 'bad', text: err.message || "Couldn't read that file. Save it again as CSV or .xlsx and retry." }); return;
    }
    const res = readCounts(text, f.name, products);
    if (res.error) { setNotice({ tone: 'bad', text: res.error }); return; }
    setUpload({ ...res, fileName: f.name });
  };
  const applyUpload = () => {
    const use = upload.matched.filter((m) => !sent[m.id]);
    setCounts((c) => { const n = { ...c }; use.forEach((m) => { n[m.id] = m.n; }); return n; });
    gpgcApi.uploaded({ game_date: gameDate, file_name: upload.fileName, filled: use.length, unmatched: upload.unmatched.length, skipped: upload.bad.length })
      .catch(() => { /* the counts are in; a missed log line isn't worth an error */ });
    setNotice({ tone: 'ok', text: `Filled in ${plural(use.length, 'count')} from ${upload.fileName}. Nothing has changed in Shopify yet.` });
    setUpload(null);
  };

  const zoomed = zoom && products.find((p) => p.id === zoom);
  const canWrite = data && data.can_write;

  return (
    <section className="uh-page uh-pgc">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Gotham FC Post-Game Count</h1>
          <div className="uh-sub">Gotham FC Shop · products on the Point of Sale channel{data && ` · Shopify read ${new Date(data.read_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} ET`}</div>
        </div>
        <button type="button" className="uh-btn" disabled={loading} onClick={() => load(gameDate)}>{loading && data ? 'Reading…' : 'Re-read Shopify'}</button>
      </div>

      {error && <div className="uh-kpc-banner bad">{error}</div>}
      {notice && <div className={`uh-kpc-banner ${notice.tone} uh-gpp-notice`}><span>{notice.text}</span><button type="button" className="uh-btn slim" onClick={() => setNotice(null)}>Dismiss</button></div>}
      {data && !canWrite && <div className="uh-kpc-banner warn">This is a dev server: the sheet is live, but updating Shopify only runs on the production Hub.</div>}
      {!data && !error && <div className="uh-card"><div className="uh-empty">Reading the Gotham FC Shop and the game's sales…</div></div>}

      {data && (
        <>
          <div className="uh-card uh-pgc-formula">
            <label className="game">Game
              <select className="uh-select" value={gameDate || ''} disabled={loading} onChange={(e) => load(e.target.value)}>
                {!data.games.length && <option value="">No home game with sales on file</option>}
                {data.games.map((g) => <option key={g.game_date} value={g.game_date}>{gameText(g)} · {num(g.units)} units sold in-stadium</option>)}
              </select>
            </label>
            <div className="eq" aria-label="How Expected is worked out">
              <span className="term"><b>Eatontown</b><small>Shopify count now</small></span><span className="op">−</span>
              <span className="term"><b>Sold at SI</b><small>POS sales at the game</small></span><span className="op">=</span>
              <span className="term hl"><b>Expected</b><small>what should be on the shelf</small></span><span className="op">vs</span>
              <span className="term"><b>Counted</b><small>what you count</small></span>
            </div>
          </div>

          {data.unmatched_sales.length > 0 && (
            <details className="uh-kpc-banner warn uh-pgc-unmatched">
              <summary>{plural(data.unmatched_sales.reduce((a, r) => a + r.units, 0), 'unit')} sold at the game didn't match a size on this sheet, so they aren't in Sold at SI. Show them</summary>
              <ul>{data.unmatched_sales.map((r) => <li key={`${r.item}|${r.size}`}>{r.item}{r.size !== 'One size' ? `, ${r.size}` : ''}: {num(r.units)}</li>)}</ul>
              <div className="sm">Usually a product renamed after the game, or one that isn't on the POS channel now.</div>
            </details>
          )}

          <div className="uh-card uh-pgc-bar">
            <div className="row">
              <div className="uh-search"><input type="search" placeholder="Search product, size, SKU or barcode" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" style={{ paddingLeft: 10 }} /></div>
              <div className="uh-gc-seg" role="radiogroup" aria-label="Show">
                {[['all', 'All'], ['todo', 'Not counted'], ['open', 'Not updated yet']].map(([v, t]) => (
                  <button key={v} type="button" role="radio" aria-checked={filter === v} onClick={() => setFilter(v)}>{t}</button>
                ))}
              </div>
            </div>
            <div className="uh-pgc-paper">
              <span><b>Counting on paper?</b> Download the blank count sheet, print it, write each count in the Counted column, then type the numbers into the file and upload it. Rows match by barcode, SKU, or product and size.</span>
              <span className="btns">
                <span className="uh-gc-seg" role="radiogroup" aria-label="Blank count sheet">
                  <button type="button" role="radio" aria-checked={photos} onClick={() => setPhotos(true)}>With photos</button>
                  <button type="button" role="radio" aria-checked={!photos} onClick={() => setPhotos(false)}>Without photos</button>
                </span>
                <button type="button" className="uh-btn primary slim" disabled={!!busy} onClick={() => exportFile(photos ? 'xlsx' : 'csv', true)}>
                  {busy === 'blank' ? 'Preparing…' : `Download blank count sheet (${photos ? 'Excel' : 'CSV'})`}
                </button>
                <button type="button" className="uh-btn slim" onClick={() => fileRef.current && fileRef.current.click()}>Upload filled-in sheet</button>
              </span>
              <span className="sm">{photos
                ? 'With photos: an Excel file with each product\'s picture next to its sizes, about 3 MB; it takes a few seconds to prepare.'
                : 'Without photos: a plain CSV, quick to download and print, and easiest to fill in on a laptop.'} Either one uploads back as it is.</span>
            </div>
            <div className="row">
              <div className="progress">
                <div className="meter"><i style={{ width: `${totals.sizes ? (100 * totals.upd) / totals.sizes : 0}%` }} /></div>
                <span><b>{num(totals.upd)} / {num(totals.sizes)}</b> sizes updated in Shopify · {plural(totals.waiting, 'counted size')} waiting in {plural(totals.ready.length, 'product')}</span>
              </div>
              <div className="acts">
                <button type="button" className="uh-btn" onClick={() => fileRef.current && fileRef.current.click()}>Upload counts (CSV or Excel)</button>
                <input ref={fileRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={onFile} />
                <button type="button" className="uh-btn" disabled={!!busy} onClick={() => exportFile('xlsx')}>{busy === 'xlsx' ? 'Preparing Excel…' : 'Excel (.xlsx)'}</button>
                <button type="button" className="uh-btn" disabled={!!busy} onClick={() => exportFile('csv')}>{busy === 'csv' ? 'Preparing CSV…' : 'CSV'}</button>
                <button type="button" className="uh-btn" disabled={!totals.ready.length || !canWrite} onClick={() => setReview(totals.ready)}>Update all counted…</button>
              </div>
            </div>
          </div>

          <div className="uh-pgc-list">
            {shown.map((p) => {
              const st = state(p); const n = st.pending.length;
              const anyNeg = p.variants.some((v) => !sent[v.id] && expected(v) < 0);
              const pill = st.allUpd ? <span className="uh-gpp-pill on">Updated in Shopify · {st.last}</span>
                : st.upd ? <span className="uh-gpp-pill on">{st.upd} of {st.total} sizes updated</span>
                  : st.counted ? <span className="uh-pgc-pill warn">{st.counted} of {st.total} sizes counted</span>
                    : <span className="uh-gpp-pill off">Not counted</span>;
              return (
                <article key={p.id} className={`uh-pgc-card${st.allUpd ? ' done' : ''}`}>
                  <div className="ph"><Photo src={p.image} alt={p.title} onZoom={() => setZoom(p.id)} /></div>
                  <div className="body">
                    <div className="ttl">
                      <div><h2>{p.title}</h2>{p.collections.length > 0 && <div className="uh-gpp-chips">{p.collections.map((c) => <span key={c} className="chip">{c}</span>)}</div>}</div>
                      {pill}
                    </div>
                    <div className="uh-tscroll"><table className="uh-tbl uh-pgc-sizes">
                      <thead><tr><th>Size</th><th className="code">SKU / barcode</th><th className="r">Eatontown</th><th className="r">Sold at SI</th><th className="r exp">Expected</th><th className="r">Counted</th></tr></thead>
                      <tbody>
                        {p.variants.map((v) => {
                          const s = sent[v.id]; const c = s ? s.counted : counts[v.id];
                          return (
                            <tr key={v.id}>
                              <td className="size">{v.title}</td>
                              <td className="code uh-mono">{v.sku || '—'}{v.barcode && <div>{v.barcode}</div>}</td>
                              <td className="r uh-num">{fmtCount(eatShown(v))}</td>
                              <td className="r uh-num">{num(v.sold)}</td>
                              <td className="r uh-num exp">{fmtCount(expected(v))}</td>
                              <td className="r">
                                <input className={`uh-pgc-cnt${c !== undefined ? ' filled' : ''}`} type="number" inputMode="numeric" min="0" step="1"
                                  value={c === undefined ? '' : c} disabled={!!s || !v.tracked} aria-label={`Counted ${label(p, v)}`}
                                  onChange={(e) => setCount(v.id, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key !== 'Enter') return;
                                    e.preventDefault();
                                    const all = [...document.querySelectorAll('.uh-pgc-cnt:not(:disabled)')]; const nx = all[all.indexOf(e.target) + 1];
                                    if (nx) nx.focus();
                                  }} />
                                {s && <div className="uh-pgc-sent">✓ In Shopify</div>}
                                {!v.tracked && <div className="uh-pgc-ns">Not tracked</div>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table></div>
                    <div className="tools">
                      {st.allUpd ? (
                        <><span>Eatontown was set to these counts and SI Stadium to 0.</span><button type="button" className="uh-btn slim" onClick={() => countAgain(p)}>Count again</button></>
                      ) : (
                        <>
                          <button type="button" className="uh-btn primary slim" disabled={!n || !canWrite} onClick={() => setReview([p.id])}>
                            {n ? `Review & update ${plural(n, 'size')} in Shopify` : 'Review & update in Shopify'}
                          </button>
                          {!n && <span>Enter a count to update.</span>}
                          {st.upd > 0 && <button type="button" className="uh-btn slim" onClick={() => countAgain(p)}>Count again</button>}
                          {anyNeg
                            ? <span>Expected is below 0 for a size here, so Shopify's Eatontown count is already off. Count it by hand.</span>
                            : <button type="button" className="uh-btn slim" onClick={() => fill(p, (v) => expected(v))}>Matches expected</button>}
                          <button type="button" className="uh-btn slim" onClick={() => fill(p, () => 0)}>None left (all 0)</button>
                          {n > 0 && <button type="button" className="uh-btn slim" onClick={() => fill(p, () => null)}>Clear</button>}
                        </>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
            {!shown.length && <div className="uh-card"><div className="uh-empty">No products match. Clear the search or pick another filter.</div></div>}
          </div>
          <p className="uh-pgc-hint">In-Stadium Jersey Customization isn't listed: it's the name-and-number service rung at the stand, not shelf stock. A size not stocked at Eatontown is stocked there when its count is sent.</p>
        </>
      )}

      {zoomed && (
        <Dialog title={zoomed.title} onClose={() => setZoom(null)}>
          <img className="uh-pgc-big" src={zoomed.image} alt={zoomed.title} />
        </Dialog>
      )}

      {review && (() => {
        const rows = reviewLines(review); const one = review.length === 1; const p0 = products.find((x) => x.id === review[0]);
        const left = review.reduce((a, id) => { const st = state(products.find((x) => x.id === id)); return a + st.total - st.counted; }, 0);
        return (
          <Dialog title={one ? p0.title : `Update ${plural(review.length, 'product')} in Shopify`} busy={busy === 'apply'} onClose={() => setReview(null)}
            footer={<>
              <button type="button" className="uh-btn" disabled={busy === 'apply'} onClick={() => setReview(null)}>Keep counting</button>
              <button type="button" className="uh-btn primary" disabled={busy === 'apply' || !rows.length} onClick={applyNow}>
                {busy === 'apply' ? 'Updating…' : `Update ${plural(rows.length, 'size')} in Shopify`}
              </button>
            </>}>
            {one && p0.image && <img className="uh-pgc-rvimg" src={p0.image} alt={p0.title} />}
            <div className="uh-kpc-banner">
              <b>{data.from}</b> is set to what you counted and <b>{data.to}</b> goes back to <b>0</b>, so the next game's sales start fresh. Both use the reason Correction.
              If a size sold or changed in Shopify since the page read it, it's skipped and listed so you can re-check it.
            </div>
            {left > 0 && <div className="uh-kpc-banner warn">{plural(left, 'size')} {left === 1 ? "isn't" : "aren't"} counted yet and stay{left === 1 ? 's' : ''} as {left === 1 ? 'it is' : 'they are'} in Shopify. You can count and update {left === 1 ? 'it' : 'them'} later.</div>}
            <div className="uh-tscroll"><table className="uh-tbl">
              <thead><tr>{!one && <th>Product</th>}<th>Size</th><th className="r">Expected</th><th className="r">Eatontown</th><th className="r">SI Stadium</th></tr></thead>
              <tbody>{rows.map(({ p, v }) => (
                <tr key={v.id}>
                  {!one && <td>{p.title}</td>}
                  <td>{v.title}</td>
                  <td className="r uh-num">{num(expected(v))}</td>
                  <td className="r uh-num">{v.eatontown == null ? '—' : num(v.eatontown)} → <b>{num(counts[v.id])}</b></td>
                  <td className="r uh-num">{v.stadium == null ? '—' : `${num(v.stadium)} → `}<b>{v.stadium == null ? '' : '0'}</b></td>
                </tr>
              ))}</tbody>
            </table></div>
          </Dialog>
        );
      })()}

      {result && (
        <Dialog title="Shopify updated" onClose={() => setResult(null)}
          footer={<><button type="button" className="uh-btn" onClick={() => { setResult(null); load(gameDate); }}>Re-read Shopify</button><button type="button" className="uh-btn primary" onClick={() => setResult(null)}>Done</button></>}>
          {result.set.length > 0 && <div className="uh-kpc-banner ok"><b>{plural(result.set.length, 'size')} updated.</b> Eatontown now matches your counts and SI Stadium is back to 0 for them.</div>}
          {result.stale.length > 0 && (
            <div className="uh-kpc-banner warn"><b>{plural(result.stale.length, 'size')} changed in Shopify after the page read it, so {result.stale.length === 1 ? 'it was' : 'they were'} not updated.</b> The page now shows Shopify's current numbers. Re-check, then update again.
              <ul>{result.stale.map((ln) => <li key={ln.variant_id}>{ln.label}{ln.now ? `: Eatontown ${ln.eatontown ?? '—'} → ${ln.now.eatontown ?? '—'}, SI Stadium ${ln.stadium ?? '—'} → ${ln.now.stadium ?? '—'}` : ''}</li>)}</ul>
            </div>
          )}
          {result.failed.length > 0 && (
            <div className="uh-kpc-banner bad"><b>{plural(result.failed.length, 'size')} failed:</b>
              <ul>{result.failed.map((ln) => <li key={ln.variant_id}>{ln.label}: {ln.error}</li>)}</ul>
            </div>
          )}
        </Dialog>
      )}

      {upload && (() => {
        const use = upload.matched.filter((m) => !sent[m.id]); const locked = upload.matched.length - use.length;
        const replaced = use.filter((m) => counts[m.id] !== undefined && counts[m.id] !== m.n).length;
        const list = (items, f) => <ul>{items.slice(0, 8).map(f)}{items.length > 8 && <li>and {num(items.length - 8)} more</li>}</ul>;
        return (
          <Dialog title={`Counts from ${upload.fileName}`} onClose={() => setUpload(null)}
            footer={<><button type="button" className="uh-btn" onClick={() => setUpload(null)}>Cancel</button><button type="button" className="uh-btn primary" disabled={!use.length} onClick={applyUpload}>Fill in {plural(use.length, 'count')}</button></>}>
            <div className="uh-kpc-banner">These fill in the Counted boxes. Nothing changes in Shopify yet: check the products, then update each one, or use Update all counted.
              {replaced > 0 && <> <b>{plural(replaced, 'count')} you already typed will be replaced</b> by the file's number.</>}</div>
            {upload.unmatched.length > 0 && <div className="uh-kpc-banner warn"><b>{plural(upload.unmatched.length, 'row')} didn't match a size on the sheet</b> (check the barcode, SKU, or product and size name):{list(upload.unmatched, (u) => <li key={u.line}>Row {u.line}: {u.label}</li>)}</div>}
            {upload.bad.length > 0 && <div className="uh-kpc-banner warn"><b>Not a whole number, so skipped:</b>{list(upload.bad, (u) => <li key={u.line}>Row {u.line}: {u.label} ("{u.raw}")</li>)}</div>}
            {upload.dupes.length > 0 && <div className="uh-kpc-banner warn"><b>The same size appears more than once; the last row is used:</b>{list(upload.dupes, (u) => <li key={u.line}>Row {u.line}: {u.label}</li>)}</div>}
            {upload.blank > 0 && <div className="uh-pgc-hint">{plural(upload.blank, 'size')} left blank in the file {upload.blank === 1 ? 'was' : 'were'} skipped, not counted as 0. {upload.blank === 1 ? 'Its count stays' : 'Their counts stay'} as {upload.blank === 1 ? 'it is' : 'they are'} on this page and in Shopify.</div>}
            {locked > 0 && <div className="uh-pgc-hint">{plural(locked, 'size')} already sent to Shopify {locked === 1 ? 'is' : 'are'} left as {locked === 1 ? 'it is' : 'they are'}. Use Count again on the product to change {locked === 1 ? 'it' : 'them'}.</div>}
            <div className="uh-tscroll"><table className="uh-tbl">
              <thead><tr><th>Product</th><th>Size</th><th className="r">Expected</th><th className="r">Counted</th></tr></thead>
              <tbody>{use.map((m) => { const hit = byVariant[m.id]; return (
                <tr key={m.id}><td>{hit.p.title}</td><td>{hit.v.title}</td><td className="r uh-num">{num(expected(hit.v))}</td>
                  <td className="r uh-num">{counts[m.id] !== undefined && counts[m.id] !== m.n && <s className="uh-pgc-was">{num(counts[m.id])}</s>} <b>{num(m.n)}</b></td></tr>
              ); })}</tbody>
            </table></div>
          </Dialog>
        );
      })()}
    </section>
  );
}
